import { useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import useBlockedHashtags from "../../hooks/useBlockedHashtags";
import useCreateBlockedHashtag from "../../hooks/useCreateBlockedHashtag";
import useDeleteBlockedHashtag from "../../hooks/useDeleteBlockedHashtag";

const formatDate = (dateString: string): string => {
	return new Date(dateString).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric"
	});
};

const BlockedWordsSection = () => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

	const { data: blockedWords = [], isLoading } = useBlockedHashtags();
	const { mutate: createWord, isLoading: isCreating } =
		useCreateBlockedHashtag();
	const { mutate: deleteWord } = useDeleteBlockedHashtag();

	const handleAdd = () => {
		const trimmed = inputValue.trim();
		if (!trimmed) return;
		createWord(trimmed, {
			onSuccess: () => setInputValue("")
		});
	};

	const handleDelete = (id: string) => {
		setDeletingIds((prev) => {
			const next = new Set(prev);
			next.add(id);
			return next;
		});
		deleteWord(id, {
			onSettled: () =>
				setDeletingIds((prev) => {
					const next = new Set(prev);
					next.delete(id);
					return next;
				})
		});
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleAdd();
		}
	};

	return (
		<div className="mt-4 rounded-xl border border-border bg-subtle shadow-sm">
			<button
				type="button"
				onClick={() => setIsExpanded((prev) => !prev)}
				className="flex w-full items-center justify-between px-6 py-4 text-left"
				aria-expanded={isExpanded}
				aria-controls="blocked-words-panel"
			>
				<span className="font-heading text-lg font-medium text-foreground">
					Blocked Words
				</span>
				{isExpanded ? (
					<ChevronDown
						className="h-5 w-5 text-muted-foreground"
						aria-hidden="true"
					/>
				) : (
					<ChevronRight
						className="h-5 w-5 text-muted-foreground"
						aria-hidden="true"
					/>
				)}
			</button>

			{isExpanded && (
				<div id="blocked-words-panel" className="px-6 pb-6">
					<p className="text-sm text-muted-foreground mb-4">
						Words added here will be blocked from being used as hashtags by
						users. Common profanities are blocked automatically — you only need
						to add additional words specific to your organisation.
					</p>

					{/* Add input */}
					<div className="flex items-center gap-2 mb-6">
						<Input
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							onKeyDown={handleKeyDown}
							placeholder="Enter a word to block..."
							aria-label="Word to block"
							className="bg-white max-w-xs"
							disabled={isCreating}
						/>
						<Button
							type="button"
							onClick={handleAdd}
							disabled={!inputValue.trim() || isCreating}
						>
							{isCreating ? (
								<>
									<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
									<span className="sr-only">Adding...</span>
								</>
							) : (
								"Add"
							)}
						</Button>
					</div>

					{/* Table */}
					{isLoading ? (
						<p role="status" className="text-sm text-muted-foreground">
							Loading...
						</p>
					) : blockedWords.length === 0 ? (
						<p role="status" className="text-sm text-muted-foreground">
							No blocked words yet.
						</p>
					) : (
						<div className="rounded-lg border border-border overflow-hidden">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b border-border bg-white">
										<th className="px-4 py-3 text-left font-medium text-zinc-600">
											Word
										</th>
										<th className="px-4 py-3 text-left font-medium text-zinc-600">
											Added
										</th>
										<th className="px-4 py-3 w-16" aria-label="Actions" />
									</tr>
								</thead>
								<tbody>
									{blockedWords.map((item, index) => (
										<tr
											key={item.id}
											className={index % 2 === 0 ? "bg-white" : "bg-subtle"}
										>
											<td className="px-4 py-3 font-mono text-foreground">
												{item.word}
											</td>
											<td className="px-4 py-3 text-zinc-600">
												{formatDate(item.created_at)}
											</td>
											<td className="px-4 py-3">
												<button
													type="button"
													aria-label={`Remove "${item.word}" from blocked words`}
													disabled={deletingIds.has(item.id)}
													onClick={() => handleDelete(item.id)}
													className="inline-flex items-center justify-center w-8 h-8 rounded-md text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
												>
													{deletingIds.has(item.id) ? (
														<div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
													) : (
														<Trash2 className="w-4 h-4" aria-hidden="true" />
													)}
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default BlockedWordsSection;
